import { useState, useEffect } from 'react';

interface LogoIntroProps {
  onComplete: () => void;
}

export default function LogoIntro({ onComplete }: LogoIntroProps) {
  const [phase, setPhase] = useState<'particles' | 'logo' | 'tagline' | 'exit' | 'done'>('particles');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('logo'), 900);
    const t2 = setTimeout(() => setPhase('tagline'), 2400);
    const t3 = setTimeout(() => setPhase('exit'), 5000);
    const t4 = setTimeout(() => {
      setPhase('done');
      onComplete();
    }, 6500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [onComplete]);

  if (phase === 'done') return null;

  const isExiting = phase === 'exit';

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden transition-all duration-1400 ${
        isExiting ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
      }`}
      style={{
        background: 'linear-gradient(135deg, #0c0a08 0%, #1a1208 40%, #0f0c06 70%, #1c1408 100%)',
        transitionDuration: '1400ms',
      }}
    >
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: 'linear-gradient(rgba(212,175,55,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            animation: 'gridPulse 3s ease-in-out infinite',
          }}
        />
      </div>

      {/* Radial glow behind logo */}
      <div
        className={`absolute w-[600px] h-[600px] rounded-full transition-all duration-1000 ${
          phase === 'particles' ? 'opacity-0 scale-50' : 'opacity-100 scale-100'
        }`}
        style={{
          background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 40%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      {/* Orbiting ring */}
      <div
        className={`absolute w-80 h-80 rounded-full border border-amber-400/20 transition-all duration-1000 ${
          phase === 'particles' ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
        }`}
        style={{ animation: 'spin 8s linear infinite' }}
      />
      <div
        className={`absolute w-96 h-96 rounded-full border border-amber-300/10 transition-all duration-1200 ${
          phase === 'particles' ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
        }`}
        style={{ animation: 'spinReverse 12s linear infinite' }}
      />

      {/* Floating gold particles */}
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${Math.random() * 4 + 2}px`,
            height: `${Math.random() * 4 + 2}px`,
            background: `rgba(212,175,55,${Math.random() * 0.6 + 0.2})`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `floatParticle ${Math.random() * 4 + 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 2}s`,
            boxShadow: '0 0 6px rgba(212,175,55,0.5)',
          }}
        />
      ))}

      {/* Corner decorative lines */}
      {[
        'top-8 left-8 border-t border-l',
        'top-8 right-8 border-t border-r',
        'bottom-8 left-8 border-b border-l',
        'bottom-8 right-8 border-b border-r',
      ].map((cls, i) => (
        <div
          key={i}
          className={`absolute w-16 h-16 border-amber-400/30 ${cls} transition-all duration-700`}
          style={{ transitionDelay: `${i * 100}ms` }}
        />
      ))}

      {/* Main content */}
      <div className="relative flex flex-col items-center gap-6">
        {/* Logo image */}
        <div
          className={`relative transition-all duration-700 ${
            phase === 'particles'
              ? 'opacity-0 scale-50 blur-sm'
              : 'opacity-100 scale-100 blur-0'
          }`}
          style={{ transitionDelay: '0ms' }}
        >
          {/* Glow ring around logo */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)',
              filter: 'blur(20px)',
              transform: 'scale(1.5)',
              animation: 'glowPulse 2s ease-in-out infinite',
            }}
          />
          <div
            className="relative w-36 h-36 rounded-full overflow-hidden border-2 border-amber-400/40 shadow-2xl"
            style={{ boxShadow: '0 0 60px rgba(212,175,55,0.3), 0 0 120px rgba(212,175,55,0.1)' }}
          >
            <img
              src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
              alt="House of FMR"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Brand name — letter by letter */}
        <div
          className={`flex flex-col items-center gap-1 transition-all duration-700 ${
            phase === 'particles' ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          <div className="flex items-center gap-1">
            {'HOUSE OF FMR'.split('').map((char, i) => (
              <span
                key={i}
                className={`text-4xl md:text-5xl font-light tracking-[0.3em] transition-all duration-500 ${
                  char === ' ' ? 'w-4' : ''
                } ${
                  phase === 'particles'
                    ? 'opacity-0 translate-y-4'
                    : 'opacity-100 translate-y-0'
                }`}
                style={{
                  transitionDelay: `${200 + i * 60}ms`,
                  fontFamily: "'Cormorant Garamond', serif",
                  background: 'linear-gradient(135deg, #d4af37 0%, #f5e27a 40%, #d4af37 70%, #b8960c 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: 'none',
                  filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.4))',
                }}
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </div>

          {/* Divider line */}
          <div
            className={`transition-all duration-700 ${
              phase === 'tagline' || phase === 'exit'
                ? 'w-48 opacity-100'
                : 'w-0 opacity-0'
            }`}
            style={{
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.8), transparent)',
              transitionDelay: '100ms',
            }}
          />

          {/* Tagline */}
          <p
            className={`text-xs tracking-[0.4em] uppercase transition-all duration-700 ${
              phase === 'tagline' || phase === 'exit'
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-3'
            }`}
            style={{
              color: 'rgba(212,175,55,0.7)',
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 300,
              transitionDelay: '200ms',
            }}
          >
            Events &amp; Styling
          </p>
        </div>

        {/* Loading bar */}
        <div
          className={`transition-all duration-500 ${
            phase === 'tagline' || phase === 'exit' ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="w-48 h-px bg-amber-900/40 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                background: 'linear-gradient(90deg, #d4af37, #f5e27a)',
                animation: phase === 'tagline' ? 'loadBar 1.4s ease-out forwards' : 'none',
                width: phase === 'exit' ? '100%' : '0%',
                boxShadow: '0 0 8px rgba(212,175,55,0.6)',
              }}
            />
          </div>
        </div>
      </div>

      {/* CSS keyframes */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&family=Montserrat:wght@300;400&display=swap');

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spinReverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes floatParticle {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.4; }
          50% { transform: translateY(-20px) scale(1.3); opacity: 1; }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.6; transform: scale(1.5); }
          50% { opacity: 1; transform: scale(1.8); }
        }
        @keyframes gridPulse {
          0%, 100% { opacity: 0.08; }
          50% { opacity: 0.15; }
        }
        @keyframes loadBar {
          0% { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
}
